export const STEP_UP_TYPE = "urn:wylo-rc:error:step-up";

export class StepUpRequired extends Error {
  constructor() {
    super("Sign in again");
    this.name = "StepUpRequired";
  }
}

type Handler = () => void | Promise<void>;

let handler: Handler | null = null;
let inflight: Promise<void> | null = null;

export function registerStepUp(next: Handler | null): void {
  handler = next;
  inflight = null;
}

export function isStepUpProblem(status: number, body: unknown): boolean {
  if (status !== 401 || !body || typeof body !== "object") return false;
  return (body as { type?: unknown }).type === STEP_UP_TYPE;
}

export function isStepUpError(cause: unknown): cause is StepUpRequired {
  return cause instanceof StepUpRequired;
}

export function visibleFailure(cause: unknown, fallback: string): string | null {
  if (isStepUpError(cause)) return "Sign in again";
  if (cause instanceof Error && cause.message.length > 0) return cause.message;
  return fallback;
}

export async function demandStepUp(): Promise<never> {
  try {
    if (handler) {
      if (!inflight) {
        const run = Promise.resolve()
          .then(() => handler?.())
          .finally(() => {
            if (inflight === run) inflight = null;
          });
        inflight = run;
      }
      await inflight;
    }
  } catch {
    /* The problem body stays off the page even when the redirect itself fails. */
  }
  throw new StepUpRequired();
}

async function hasStepUpBody(response: Response): Promise<boolean> {
  if (response.status !== 401) return false;
  try {
    return isStepUpProblem(response.status, await response.clone().json());
  } catch {
    return false;
  }
}

export async function throwIfStepUp(response: Response): Promise<void> {
  if (await hasStepUpBody(response)) throw new StepUpRequired();
}

export async function rejectIfStepUp(response: Response): Promise<void> {
  if (await hasStepUpBody(response)) await demandStepUp();
}
